import React, {useEffect, useRef, useState} from "react";
import {State, ViewStates} from "@common/components/view-states";
import {ViewState} from "@common/models/enums/viev-state.enum";
import {useArticleForm} from "../../hooks/use-article-form.hook";
import {useExerciseApi} from "../../../core/hooks/use-exercise-api";
import {ExerciseDetails} from "../../../core/models/interfaces/exercise-details.interface";
import ExerciseCard from "../exercise-card";
import SelectedExercisePill from "../selected-exercise-pill";
import ExerciseSearchbar from "../exercise-searchbar";
import {useLoading} from "../../../core/hooks/use-loading.hook";
import {LoadingKeys} from "../../../core/models/enums/loading-keys.enum";

const ArticleFormExercises = () => {
    const {form} = useArticleForm();
    const {searchExercises, error} = useExerciseApi();
    const {isLoading} = useLoading();

    const [results, setResults] = useState<ExerciseDetails[]>([]);
    const [query, setQuery] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const LIMIT = 20;
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const selectedExercises = form.watch("exercises") as Array<{ id: string; title: string, url?: string }>;

    const isSelected = (id: string) => selectedExercises?.some((e) => e.id === id);

    const toggleExercise = (exercise: ExerciseDetails) => {
        const current = selectedExercises ?? [];
        if (current.some((e) => e.id === exercise.id)) {
            form.setValue(
                "exercises",
                current.filter((e) => e.id !== exercise.id),
                {shouldDirty: true, shouldValidate: true}
            );
        } else {
            form.setValue(
                "exercises",
                [...current, exercise],
                {shouldDirty: true, shouldValidate: true}
            );
        }
    };

    const hasMore = results.length < totalCount;
    const isInitialLoading = isLoading(LoadingKeys.USE_EXERCISE_API) && results.length === 0;

    const loadMore = async () => {
        if (!hasMore || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const res = await searchExercises(query, LIMIT, offset);
            setResults((prev) => [...prev, ...(res.exercises ?? [])]);
            setOffset((prev) => prev + LIMIT);
        } catch (e) {
            console.error("Error loading more exercises", e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const loadInitialExercises = async () => {
        try {
            const res = await searchExercises("", LIMIT, 0);
            const safe = res ?? {exercises: [], totalCount: 0};
            setResults(safe.exercises);
            setTotalCount(safe.totalCount);
            setOffset(safe.exercises.length);
            setQuery("");
        } catch (e) {
            console.error("Error loading initial exercises", e);
        }
    };

    useEffect(() => {
        void loadInitialExercises();
    }, []);

    useEffect(() => {
        if (!sentinelRef.current) return;
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    void loadMore();
                }
            },
            {root: null, rootMargin: "200px", threshold: 0}
        );
        observerRef.current.observe(sentinelRef.current);
        return () => observerRef.current?.disconnect();
    }, [query, offset, totalCount, results.length]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Ćwiczenia</h2>
                <span className="font-semibold text-lg">
                    Wybierz ćwiczenia, które chcesz powiązać z artykułem. Wybrane ćwiczenia zostaną wyświetlone w artykule.
                </span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center flex-wrap">
                    <div className="flex gap-4 items-center w-full">
                        <ExerciseSearchbar
                            emitResults={(res, q) => {
                                const safe = res ?? {exercises: [], totalCount: 0};
                                setResults(safe.exercises);
                                setTotalCount(safe.totalCount);
                                setOffset(safe.exercises.length);
                                setQuery(q);
                            }}
                            onQueryChange={setQuery}
                        />
                    </div>
                    <div>
                        {(selectedExercises ?? []).map((e) => (
                            <SelectedExercisePill key={e.id} url={e.url} id={e.id} title={e.title}/>
                        ))}
                    </div>
                </div>
            </div>
            <div className="pt-6">
                <ViewStates
                    loading={isInitialLoading}
                    error={error}
                    data={results}
                >
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        {query ? (
                            <span className="text-on-surface/60">Brak wyników dla „{query}”.</span>
                        ) : (
                            <span className="text-on-surface/60">Wpisz, aby wyszukać ćwiczenia.</span>
                        )}
                    </State>
                    <State state={ViewState.FETCHED}>
                        <div className="space-y-3">
                            {results.map((exercise) => (
                                <ExerciseCard
                                    key={exercise.id}
                                    exercise={exercise}
                                    selected={isSelected(exercise.id)}
                                    onToggle={() => toggleExercise(exercise)}
                                />
                            ))}
                            {isLoadingMore && (
                                <div className="flex items-center justify-center py-4 text-on-surface/60">
                                    <div
                                        className="h-5 w-5 mr-2 rounded-full border-2 border-outline-mild border-t-primary animate-spin"/>
                                    <span>Ładowanie...</span>
                                </div>
                            )}
                            <div ref={sentinelRef}/>
                        </div>
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>
        </>
    );
};

export default ArticleFormExercises;

